import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Buildings, MapPin, MagnifyingGlass, DownloadSimple, SealCheck, GraduationCap, CaretRight } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SuggestCorrection from "@/components/SuggestCorrection";

export default function KeralaSchools() {
  useSeo("Kerala Schools by District", "Browse every Kerala school on AlumniPulse, grouped by district, with links to each institution's alumni page.");
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(() => new Set());

  useEffect(() => {
    api.get("/public/institutions-by-district", { params: { state: "Kerala", country: "India" } })
      .then((r) => {
        setData(r.data);
        if (r.data?.districts?.length) setOpen(new Set([r.data.districts[0].district]));
      }).catch(() => setData(false));
  }, []);

  const toggle = (d) => setOpen((prev) => {
    const n = new Set(prev);
    n.has(d) ? n.delete(d) : n.add(d);
    return n;
  });
  const jumpTo = (d) => {
    setOpen((prev) => new Set(prev).add(d));
    setTimeout(() => document.getElementById(`dist-${d.replace(/\s+/g, "-").toLowerCase()}`)?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  const districts = useMemo(() => {
    if (!data || !data.districts) return [];
    const term = q.trim().toLowerCase();
    if (!term) return data.districts;
    return data.districts
      .map((d) => ({ ...d, schools: d.schools.filter((s) => s.name.toLowerCase().includes(term) || (s.location || "").toLowerCase().includes(term)) }))
      .filter((d) => d.schools.length > 0);
  }, [data, q]);

  const downloadMarkdown = () => {
    if (!data) return;
    const origin = window.location.origin;
    const lines = [`# Kerala Schools on AlumniPulse`, ``, `_${data.total} institutions across ${data.districts.length} districts_`, ``];
    data.districts.forEach((d) => {
      lines.push(`## ${d.district} (${d.count})`, ``);
      d.schools.forEach((s) => lines.push(`- [${s.name}](${origin}/institutions/${s.id})${s.location ? ` — ${s.location}` : ""}`));
      lines.push(``);
    });
    const blob = new Blob([lines.join("\n")], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "kerala-schools-by-district.md";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mx-auto max-w-6xl px-5 py-14">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <p className="overline text-secondary mb-3">Directory · India</p>
          <h1 className="font-heading text-4xl font-light tracking-tight">Kerala schools by district</h1>
          <p className="mt-3 text-muted-foreground">
            {data ? `${data.total} schools across ${data.districts.length} districts` : "Loading…"} — every institution links to its alumni page.
            Something not right? Suggest a correction and we'll review it.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={downloadMarkdown} variant="outline" className="rounded-full gap-2" disabled={!data} data-testid="kerala-download-md">
            <DownloadSimple size={16} /> Download list (Markdown)
          </Button>
          <SuggestCorrection entityType="list" entityName="Kerala schools directory" variant="button" label="Suggest a correction" />
        </div>
      </div>

      <div className="relative mt-8 max-w-md">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by school name or place…" className="rounded-full pl-10" data-testid="kerala-filter" />
      </div>

      {data === false ? <p className="mt-10 text-muted-foreground">Couldn't load the directory. Please try again.</p>
        : data === null ? <p className="mt-10 text-muted-foreground">Loading…</p>
        : (
          <>
            {/* District jump nav */}
            <div className="mt-8 flex flex-wrap gap-2" data-testid="kerala-district-nav">
              {data.districts.map((d) => (
                <button key={d.district} onClick={() => jumpTo(d.district)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-foreground transition-colors hover:border-green-400 hover:bg-green-50">
                  {d.district}<span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] text-muted-foreground">{d.count}</span>
                </button>
              ))}
            </div>

            {districts.length === 0 && <p className="mt-10 text-muted-foreground">No schools match “{q}”.</p>}

            {districts.map((d) => {
              const isOpen = !!q.trim() || open.has(d.district);
              return (
              <section key={d.district} id={`dist-${d.district.replace(/\s+/g, "-").toLowerCase()}`} className="mt-8 scroll-mt-24" data-testid={`kerala-district-${d.district.replace(/\s+/g, "-").toLowerCase()}`}>
                <button onClick={() => toggle(d.district)} disabled={!!q.trim()}
                  className="flex w-full items-center gap-2 border-b border-border/60 pb-2 text-left"
                  data-testid={`kerala-district-toggle-${d.district.replace(/\s+/g, "-").toLowerCase()}`}>
                  <CaretRight size={18} weight="bold" className={`text-orange-500 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                  <MapPin size={18} weight="fill" className="text-orange-500" />
                  <h2 className="font-heading text-xl font-medium">{d.district}</h2>
                  <Badge variant="outline" className="rounded-full border-green-200 bg-green-50 font-normal text-green-800">{d.count} schools</Badge>
                </button>
                {isOpen && (
                  <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {d.schools.map((s) => (
                      <Link key={s.id} to={`/institutions/${s.id}`} data-testid={`kerala-school-${s.id}`}
                        className="group flex items-start gap-3 rounded-xl border border-slate-100 bg-white p-3 shadow-sm transition-all hover:-translate-y-0.5 hover:border-green-300 hover:shadow-md">
                        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-green-50 to-orange-50">
                          <GraduationCap size={18} weight="duotone" className="text-green-600" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1 text-sm font-medium leading-tight text-foreground group-hover:text-primary">
                            {s.name}{s.verified && <SealCheck size={13} weight="fill" className="shrink-0 text-primary" />}
                          </span>
                          {s.location && <span className="mt-0.5 block truncate text-xs text-muted-foreground">{s.location}</span>}
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
              </section>
              );
            })}
          </>
        )}
    </div>
  );
}
